/**
 * A value the viewer may or may not be allowed to see. Mirrors the admin graph's `Restricted`
 * union (ADR 0019): the thing exists, and knowing that is often the useful part, so restricted
 * values are rendered as placeholders and never as blanks or errors.
 */
export type RestrictedReason =
    /** It belongs to a firm the viewer holds no access on. */
    | 'other-firm'
    /** The viewer's rung is below what the backend requires to read it. */
    | 'insufficient-level'
    /** The backend cannot answer this for the viewer today. */
    | 'superuser-only'
    /** The request failed for a reason unrelated to access. */
    | 'unavailable'
    /** The request has not answered yet; the rest of the page need not wait for it. */
    | 'loading';

export interface Restricted {
    restricted: true;
    reason: RestrictedReason;
}

export type Visible<T> = T | Restricted;

export const restricted = (reason: RestrictedReason): Restricted => ({ restricted: true, reason });

export function isRestricted<T>(value: Visible<T>): value is Restricted {
    return typeof value === 'object' && value !== null && 'restricted' in value && value.restricted === true;
}

export function visibleOr<T, U>(value: Visible<T>, fallback: U): T | U {
    return isRestricted(value) ? fallback : value;
}

/**
 * User-facing reason text. Written for any viewer: it never mentions privileges the viewer may not
 * know exist, so the same text serves staff and firm administrators alike.
 */
export const RESTRICTED_REASON_TEXT: Record<RestrictedReason, string> = {
    'other-firm': 'You don’t have access on the firm this belongs to.',
    'insufficient-level': 'Your level on this firm is below what is needed to see it.',
    'superuser-only': 'The backend cannot answer this for you today.',
    unavailable: 'Could not be loaded right now.',
    loading: 'Still loading.',
};
